import { z } from "zod";

/**
 * The page model's vocabulary, the TypeScript twin of profiles/design/files/scripts/design/model.py.
 *
 * The Python side is the definition; this exists so the browser can draw and constrain edits and the
 * components route can validate a saved screen without a round trip. tests/ts/page-model-css.test.ts
 * holds the two to one golden page.
 */

export const FONT_WEIGHTS = {
	Inter: [400, 500, 600, 700],
	Roboto: [400, 500, 700],
	"Open Sans": [400, 500, 600, 700],
	"Noto Sans SC": [400, 500, 700],
} as const;
export type Font = keyof typeof FONT_WEIGHTS;
export const FONTS = Object.keys(FONT_WEIGHTS) as Font[];
export const WEIGHTS = [400, 500, 600, 700] as const;
export const KINDS = ["frame", "text", "image", "button"] as const;
export const LAYOUTS = ["column", "row"] as const;
export const ALIGNS = ["start", "center", "end"] as const;
export const JUSTIFIES = ["start", "center", "end", "between"] as const;
export const TEXT_ALIGNS = ["left", "center", "right"] as const;
export const VARIANTS = ["primary", "secondary", "ghost"] as const;
export const FITS = ["cover", "contain"] as const;

const HEX = /^#[0-9a-fA-F]{6}$/;
const ID = /^e(\d+)$/;

const px = z.number().min(0);
const size = z.union([z.literal("hug"), z.literal("fill"), z.number().positive()]);
const color = z.string().min(1);
const padding = z.union([px, z.array(px).min(1).max(4).refine((p) => p.length !== 3, "1, 2 or 4 numbers")]);

const common = { id: z.string().regex(ID, "ids are e followed by a number") };

export const textSchema = z
	.object({
		...common,
		kind: z.literal("text"),
		text: z.string(),
		size: px,
		weight: z.union([z.literal(400), z.literal(500), z.literal(600), z.literal(700)]),
		color,
		lineHeight: px.optional(),
		align: z.enum(TEXT_ALIGNS).optional(),
		width: size.optional(),
	})
	.strict();

export const imageSchema = z
	.object({
		...common,
		kind: z.literal("image"),
		src: z.string(),
		width: z.number().positive(),
		height: z.number().positive(),
		radius: px.optional(),
		fit: z.enum(FITS).optional(),
	})
	.strict();

export const buttonSchema = z
	.object({
		...common,
		kind: z.literal("button"),
		label: z.string(),
		variant: z.enum(VARIANTS),
		width: size.optional(),
	})
	.strict();

export type TextElement = z.infer<typeof textSchema>;
export type ImageElement = z.infer<typeof imageSchema>;
export type ButtonElement = z.infer<typeof buttonSchema>;
export interface FrameElement {
	id: string;
	kind: "frame";
	layout: (typeof LAYOUTS)[number];
	gap?: number;
	padding?: number | number[];
	align?: (typeof ALIGNS)[number];
	justify?: (typeof JUSTIFIES)[number];
	width?: "hug" | "fill" | number;
	height?: "hug" | "fill" | number;
	fill?: string;
	stroke?: string;
	strokeWidth?: number;
	radius?: number;
	children?: Element[];
}
export type Element = FrameElement | TextElement | ImageElement | ButtonElement;
export type Kind = Element["kind"];

export const frameSchema = z
	.object({
		...common,
		kind: z.literal("frame"),
		layout: z.enum(LAYOUTS),
		gap: px.optional(),
		padding: padding.optional(),
		align: z.enum(ALIGNS).optional(),
		justify: z.enum(JUSTIFIES).optional(),
		width: size.optional(),
		height: size.optional(),
		fill: color.optional(),
		stroke: color.optional(),
		strokeWidth: px.optional(),
		radius: px.optional(),
		// Lazy only here, so the union below still sees plain objects and can discriminate on kind.
		children: z.array(z.lazy((): z.ZodType<Element> => elementSchema)).optional(),
	})
	.strict();
export const elementSchema = z.discriminatedUnion("kind", [frameSchema, textSchema, imageSchema, buttonSchema]);

export const themeSchema = z
	.object({
		font: z.enum(FONTS as [Font, ...Font[]]),
		colors: z.record(z.string().regex(HEX, "#RRGGBB")),
	})
	.strict();
export type Theme = z.infer<typeof themeSchema>;

export const screenSchema = z
	.object({
		id: z.string().min(1),
		title: z.string(),
		width: z.number().int().positive(),
		nextId: z.number().int(),
		theme: themeSchema,
		root: frameSchema,
	})
	.strict();
export type Screen = z.infer<typeof screenSchema>;

/** A screen the vocabulary cannot express, by element and property; element is null for the screen itself. */
export interface Refusal {
	element: string | null;
	property: string;
	reason: string;
}

export type ParseResult = { ok: true; screen: Screen } | { ok: false; refusal: Refusal };

/** Every node in document order, each with its parent. */
export function* walk(node: Element, parent: FrameElement | null = null): Generator<[Element, FrameElement | null]> {
	yield [node, parent];
	if (node.kind === "frame") for (const child of node.children ?? []) yield* walk(child, node);
}

/** A theme colour name or a hex, as upper-case hex. */
export function resolveColor(theme: Theme, value: string): string {
	return (theme.colors[value] ?? value).toUpperCase();
}

/** A button label is 600 where the font has it in Figma, else 700. */
export function labelWeight(theme: Pick<Theme, "font">): 600 | 700 {
	return (FONT_WEIGHTS[theme.font] as readonly number[]).includes(600) ? 600 : 700;
}

/** The screen's rules beyond its shape: ids, colours, weights, the root's width, nextId. */
export function parseScreen(input: unknown): ParseResult {
	const shape = screenSchema.safeParse(input);
	if (!shape.success) return { ok: false, refusal: refusalFromIssue(input, shape.error.issues[0]!) };
	const screen = shape.data;
	const seen = new Set<string>();
	let highest = 0;
	for (const [node, parent] of walk(screen.root)) {
		if (seen.has(node.id)) return refuse(node.id, "id", "used twice in this screen");
		seen.add(node.id);
		highest = Math.max(highest, Number(ID.exec(node.id)![1]));
		if (parent && parent.kind !== "frame") return refuse(parent.id, "children", `a ${parent.kind} has no children`);
		for (const prop of ["fill", "stroke", "color"] as const) {
			const value = (node as Record<string, unknown>)[prop];
			if (typeof value === "string" && !(value in screen.theme.colors) && !HEX.test(value)) {
				return refuse(node.id, prop, `'${value}' is neither a theme colour nor #RRGGBB`);
			}
		}
		if (node.kind === "text") {
			const allowed = FONT_WEIGHTS[screen.theme.font] as readonly number[];
			if (!allowed.includes(node.weight)) return refuse(node.id, "weight", `${node.weight}: ${screen.theme.font} has ${allowed.join(", ")} in Figma`);
		}
	}
	if (screen.nextId <= highest) return refuse(null, "nextId", `must be past the highest id in use (e${highest})`);
	if (screen.root.width !== screen.width) return refuse(screen.root.id, "width", "the root's width is the screen's width in pixels");
	return { ok: true, screen };
}

function refuse(element: string | null, property: string, reason: string): ParseResult {
	return { ok: false, refusal: { element, property, reason } };
}

/** Names the element a zod issue sits in by walking the raw input along the issue's path. */
function refusalFromIssue(input: unknown, issue: z.ZodIssue): Refusal {
	let node: unknown = input;
	let element: string | null = null;
	for (const step of issue.path) {
		node = typeof node === "object" && node !== null ? (node as Record<string | number, unknown>)[step] : undefined;
		const id = typeof node === "object" && node !== null ? (node as { id?: unknown }).id : undefined;
		if (typeof id === "string") element = id;
	}
	const last = issue.path[issue.path.length - 1];
	const property = typeof last === "string" ? last : issue.path.length === 0 ? "fields" : String(issue.path[issue.path.length - 2] ?? "fields");
	if (issue.code === "unrecognized_keys") return { element, property: issue.keys[0] ?? property, reason: `no such property` };
	if (issue.code === "invalid_enum_value" || issue.code === "invalid_literal") return { element, property, reason: `'${String(issue.received)}' is not one of ${(issue as { options?: unknown[] }).options?.join(", ") ?? issue.message}` };
	return { element, property, reason: issue.message };
}
